import React, { useEffect, useMemo, useState } from 'react';

const baseStyles = {
  shell: {
    maxWidth: 980,
    margin: '0 auto',
    padding: 24,
    fontFamily: '"Segoe UI", Arial, sans-serif',
    color: '#0f172a',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #dbe3ef',
    borderRadius: 18,
    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
    overflow: 'hidden',
  },
  hero: {
    padding: 24,
    background: 'linear-gradient(135deg, #eff6ff 0%, #ecfeff 100%)',
    borderBottom: '1px solid #dbe3ef',
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 700,
  },
  subtitle: {
    marginTop: 8,
    color: '#475569',
    lineHeight: 1.6,
  },
  body: {
    padding: 24,
  },
  grid: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
  },
  input: {
    width: '100%',
    border: '1px solid #cbd5e1',
    borderRadius: 12,
    padding: '12px 14px',
    fontSize: 15,
    outline: 'none',
    background: '#fff',
  },
  textarea: {
    width: '100%',
    border: '1px solid #cbd5e1',
    borderRadius: 12,
    padding: '12px 14px',
    fontSize: 15,
    minHeight: 120,
    resize: 'vertical',
    outline: 'none',
    background: '#fff',
  },
  sectionTitle: {
    margin: '0 0 14px',
    fontSize: 17,
    fontWeight: 700,
  },
  actions: {
    marginTop: 24,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  primaryButton: {
    border: 0,
    borderRadius: 12,
    background: '#2563eb',
    color: '#fff',
    padding: '12px 18px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  secondaryButton: {
    border: '1px solid #cbd5e1',
    borderRadius: 12,
    background: '#fff',
    color: '#0f172a',
    padding: '12px 18px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  optionCard: {
    border: '1px solid #dbe3ef',
    borderRadius: 16,
    padding: 18,
    background: '#f8fafc',
  },
  infoBanner: {
    marginTop: 16,
    padding: 14,
    borderRadius: 14,
    border: '1px solid #bfdbfe',
    background: '#eff6ff',
    color: '#1e3a8a',
    fontSize: 14,
    lineHeight: 1.6,
  },
  errorBanner: {
    marginBottom: 16,
    padding: 14,
    borderRadius: 14,
    border: '1px solid #fecaca',
    background: '#fef2f2',
    color: '#b91c1c',
    fontSize: 14,
    lineHeight: 1.6,
  },
  successBanner: {
    marginBottom: 16,
    padding: 14,
    borderRadius: 14,
    border: '1px solid #bbf7d0',
    background: '#f0fdf4',
    color: '#166534',
    fontSize: 14,
    lineHeight: 1.6,
  },
  reviewGrid: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    marginTop: 18,
  },
  reviewCard: {
    border: '1px solid #dbe3ef',
    borderRadius: 16,
    padding: 18,
    background: '#ffffff',
  },
  detailRow: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    gap: 10,
    fontSize: 14,
    lineHeight: 1.6,
    marginBottom: 8,
  },
  mutted: {
    color: '#475569',
  },
};

function injectResponsiveStyles() {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.getElementById('opd-form-responsive-styles')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'opd-form-responsive-styles';
  style.textContent = `
    @media (max-width: 640px) {
      .opd-form-shell {
        padding: 14px !important;
      }

      .opd-form-actions {
        flex-direction: column;
        align-items: stretch !important;
      }

      .opd-form-actions button {
        width: 100%;
      }
    }
  `;
  document.head.appendChild(style);
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window is not available.'));
      return;
    }

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (window.Razorpay) {
        resolve();
        return;
      }

      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Unable to load script.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Unable to load script.'));
    document.body.appendChild(script);
  });
}

async function parseJson(response) {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch (error) {
    return { message: text || 'Unexpected server response.' };
  }
}

function endpoint(baseUrl, path) {
  return `${String(baseUrl).replace(/\/$/, '')}${path}`;
}

export function OpdForm({
  apiBaseUrl = 'http://localhost:8000/api',
  title = 'Book an OPD Appointment',
  subtitle = 'Fill the form, review your details, then choose pay at hospital or online payment.',
  razorpayKeyId = '',
  onSuccess,
}) {
  const [form, setForm] = useState({
    patient_name: '',
    contact_number: '',
    appointment_date: '',
    gender: '',
    email: '',
    department: '',
    doctor: '',
    symptoms: '',
  });
  const [step, setStep] = useState('form');
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    injectResponsiveStyles();
  }, []);

  useEffect(() => {
    let ignore = false;

    async function loadDoctors() {
      setLoadingDoctors(true);
      setError('');

      try {
        const response = await fetch(endpoint(apiBaseUrl, '/doctors/public'), {
          headers: {
            Accept: 'application/json',
          },
        });
        const payload = await parseJson(response);

        if (!response.ok || !payload.success) {
          throw new Error(payload.message || 'Unable to load doctors.');
        }

        if (!ignore) {
          setDoctors(Array.isArray(payload.data) ? payload.data : []);
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || 'Unable to load doctors.');
        }
      } finally {
        if (!ignore) {
          setLoadingDoctors(false);
        }
      }
    }

    loadDoctors();

    return () => {
      ignore = true;
    };
  }, [apiBaseUrl]);

  const departments = useMemo(() => {
    const map = new Map();
    doctors.forEach((doctor) => {
      if (doctor.department?.name && !map.has(doctor.department.name)) {
        map.set(doctor.department.name, doctor.department);
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [doctors]);

  const filteredDoctors = useMemo(() => {
    if (!form.department) {
      return [];
    }

    return doctors.filter((doctor) => doctor.department?.name === form.department);
  }, [doctors, form.department]);

  const selectedDoctor = useMemo(
    () => doctors.find((doctor) => doctor.name === form.doctor),
    [doctors, form.doctor]
  );

  function updateField(name, value) {
    setForm((current) => {
      const next = { ...current, [name]: value };

      if (name === 'department') {
        next.doctor = '';
      }

      return next;
    });
  }

  function validateForm() {
    if (!form.patient_name.trim()) return 'Patient name is required.';
    if (!form.contact_number.trim()) return 'Contact number is required.';
    if (!form.appointment_date) return 'Appointment date is required.';
    if (!form.gender) return 'Gender is required.';
    if (!form.department) return 'Department is required.';
    if (!form.doctor) return 'Doctor is required.';
    if (!form.symptoms.trim()) return 'Symptoms are required.';
    return '';
  }

  async function createBooking() {
    const response = await fetch(endpoint(apiBaseUrl, '/mobile/appointments'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(form),
    });

    const payload = await parseJson(response);

    if (!response.ok || !payload.success) {
      if (payload.errors) {
        const firstGroup = Object.values(payload.errors)[0];
        if (Array.isArray(firstGroup) && firstGroup[0]) {
          throw new Error(firstGroup[0]);
        }
      }

      throw new Error(payload.message || 'Unable to create appointment.');
    }

    return payload.data;
  }

  async function handleReview(event) {
    event.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setStep('review');
  }

  function handlePayAtHospital() {
    setError('');
    setSuccess('');

    setSuccess('Thank you, we will get back to you soon.');
    setStep('complete');
    onSuccess?.(form, 'pay_at_hospital');
  }

  async function handlePayOnline() {
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      if (!razorpayKeyId) {
        throw new Error('Razorpay key ID is missing.');
      }

      await loadScript('https://checkout.razorpay.com/v1/checkout.js');

      const createdBooking = booking || (await createBooking());
      setBooking(createdBooking);

      const orderResponse = await fetch(
        endpoint(apiBaseUrl, `/mobile/appointments/${createdBooking.id}/payments/razorpay/order`),
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      const orderPayload = await parseJson(orderResponse);

      if (!orderResponse.ok || !orderPayload.success) {
        throw new Error(orderPayload.message || 'Unable to create Razorpay order.');
      }

      await new Promise((resolve, reject) => {
        const razorpay = new window.Razorpay({
          key: razorpayKeyId,
          amount: orderPayload.data.amount,
          currency: orderPayload.data.currency,
          order_id: orderPayload.data.order_id,
          name: orderPayload.data.name,
          description: orderPayload.data.description,
          prefill: orderPayload.data.prefill,
          theme: {
            color: '#2563eb',
          },
          handler: async (paymentResponse) => {
            try {
              const verifyResponse = await fetch(
                endpoint(apiBaseUrl, `/mobile/appointments/${createdBooking.id}/payments/razorpay/verify`),
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                  },
                  body: JSON.stringify(paymentResponse),
                }
              );

              const verifyPayload = await parseJson(verifyResponse);

              if (!verifyResponse.ok || !verifyPayload.success) {
                throw new Error(verifyPayload.message || 'Payment verification failed.');
              }

              setBooking(verifyPayload.data || createdBooking);
              setSuccess(verifyPayload.message || 'Payment completed successfully.');
              if (onSuccess) {
                onSuccess(verifyPayload.data || createdBooking, 'razorpay');
              }
              setStep('complete');
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          modal: {
            ondismiss: () => reject(new Error('Payment window was closed before completion.')),
          },
        });

        razorpay.on('payment.failed', (failure) => {
          reject(new Error(failure?.error?.description || 'Payment failed.'));
        });

        razorpay.open();
      });
    } catch (err) {
      setError(err.message || 'Unable to complete online payment.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={baseStyles.shell} className="opd-form-shell">
      <div style={baseStyles.card}>
        <div style={baseStyles.hero}>
          <h2 style={baseStyles.title}>{title}</h2>
          <p style={baseStyles.subtitle}>{subtitle}</p>
        </div>

        <div style={baseStyles.body}>
          {error ? <div style={baseStyles.errorBanner}>{error}</div> : null}
          {success ? <div style={baseStyles.successBanner}>{success}</div> : null}

          {step === 'form' ? (
            <form onSubmit={handleReview}>
              <div style={baseStyles.grid}>
                <div style={baseStyles.field}>
                  <label style={baseStyles.label}>Patient Name</label>
                  <input
                    style={baseStyles.input}
                    value={form.patient_name}
                    onChange={(event) => updateField('patient_name', event.target.value)}
                    placeholder="Enter patient name"
                  />
                </div>

                <div style={baseStyles.field}>
                  <label style={baseStyles.label}>Contact Number</label>
                  <input
                    style={baseStyles.input}
                    value={form.contact_number}
                    onChange={(event) => updateField('contact_number', event.target.value)}
                    placeholder="Enter contact number"
                  />
                </div>

                <div style={baseStyles.field}>
                  <label style={baseStyles.label}>Appointment Date</label>
                  <input
                    style={baseStyles.input}
                    type="date"
                    value={form.appointment_date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(event) => updateField('appointment_date', event.target.value)}
                  />
                </div>

                <div style={baseStyles.field}>
                  <label style={baseStyles.label}>Gender</label>
                  <select
                    style={baseStyles.input}
                    value={form.gender}
                    onChange={(event) => updateField('gender', event.target.value)}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={baseStyles.field}>
                  <label style={baseStyles.label}>Email (optional)</label>
                  <input
                    style={baseStyles.input}
                    type="email"
                    value={form.email}
                    onChange={(event) => updateField('email', event.target.value)}
                    placeholder="Enter email"
                  />
                </div>

                <div style={baseStyles.field}>
                  <label style={baseStyles.label}>Department</label>
                  <select
                    style={baseStyles.input}
                    value={form.department}
                    onChange={(event) => updateField('department', event.target.value)}
                    disabled={loadingDoctors}
                  >
                    <option value="">{loadingDoctors ? 'Loading departments...' : 'Select department'}</option>
                    {departments.map((department) => (
                      <option key={department.id} value={department.name}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ ...baseStyles.grid, marginTop: 16 }}>
                <div style={baseStyles.field}>
                  <label style={baseStyles.label}>Select Doctor</label>
                  <select
                    style={baseStyles.input}
                    value={form.doctor}
                    onChange={(event) => updateField('doctor', event.target.value)}
                    disabled={!form.department || loadingDoctors}
                  >
                    <option value="">
                      {!form.department ? 'Select department first' : 'Select doctor'}
                    </option>
                    {filteredDoctors.map((doctor) => (
                      <option key={doctor.id} value={doctor.name}>
                        {doctor.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ ...baseStyles.field, marginTop: 16 }}>
                <label style={baseStyles.label}>Symptoms in Brief</label>
                <textarea
                  style={baseStyles.textarea}
                  value={form.symptoms}
                  onChange={(event) => updateField('symptoms', event.target.value)}
                  placeholder="Describe symptoms"
                />
              </div>

              <div style={baseStyles.actions} className="opd-form-actions">
                <div style={baseStyles.mutted}>
                  {selectedDoctor
                    ? `Consultation fee: INR ${Number(selectedDoctor.consultation_fee || 0).toFixed(2)}`
                    : 'Select a department and doctor to continue.'}
                </div>
                <button type="submit" style={baseStyles.primaryButton}>
                  Review Booking
                </button>
              </div>
            </form>
          ) : null}

          {step === 'review' ? (
            <div>
              <h3 style={baseStyles.sectionTitle}>Review Your Booking</h3>
              <div style={baseStyles.reviewGrid}>
                <div style={baseStyles.reviewCard}>
                  <div style={baseStyles.detailRow}>
                    <span style={baseStyles.mutted}>Patient</span>
                    <strong>{form.patient_name}</strong>
                  </div>
                  <div style={baseStyles.detailRow}>
                    <span style={baseStyles.mutted}>Contact</span>
                    <strong>{form.contact_number}</strong>
                  </div>
                  <div style={baseStyles.detailRow}>
                    <span style={baseStyles.mutted}>Email</span>
                    <strong>{form.email || 'Not provided'}</strong>
                  </div>
                  <div style={baseStyles.detailRow}>
                    <span style={baseStyles.mutted}>Gender</span>
                    <strong>{form.gender || '-'}</strong>
                  </div>
                </div>

                <div style={baseStyles.reviewCard}>
                  <div style={baseStyles.detailRow}>
                    <span style={baseStyles.mutted}>Department</span>
                    <strong>{form.department}</strong>
                  </div>
                  <div style={baseStyles.detailRow}>
                    <span style={baseStyles.mutted}>Doctor</span>
                    <strong>{form.doctor}</strong>
                  </div>
                  <div style={baseStyles.detailRow}>
                    <span style={baseStyles.mutted}>Date</span>
                    <strong>{form.appointment_date}</strong>
                  </div>
                  <div style={baseStyles.detailRow}>
                    <span style={baseStyles.mutted}>Consultation Fee</span>
                    <strong>INR {Number(selectedDoctor?.consultation_fee || 0).toFixed(2)}</strong>
                  </div>
                </div>
              </div>

              <div style={baseStyles.infoBanner}>
                <strong>Symptoms:</strong> {form.symptoms}
              </div>

              <div style={{ ...baseStyles.reviewGrid, marginTop: 18 }}>
                <div style={baseStyles.optionCard}>
                  <h4 style={{ marginTop: 0, marginBottom: 8 }}>Pay at Hospital</h4>
                  <p style={{ ...baseStyles.mutted, marginTop: 0 }}>
                    Send an appointment request. Our team will contact you to confirm the appointment and payment details.
                  </p>
                  <button
                    type="button"
                    style={baseStyles.secondaryButton}
                    onClick={handlePayAtHospital}
                    disabled={submitting}
                  >
                    {submitting ? 'Processing...' : 'Submit Appointment Request'}
                  </button>
                </div>

                <div style={baseStyles.optionCard}>
                  <h4 style={{ marginTop: 0, marginBottom: 8 }}>Pay Online with Razorpay</h4>
                  <p style={{ ...baseStyles.mutted, marginTop: 0 }}>
                    Complete the booking online using Razorpay. This requires a configured Razorpay key and public payment endpoints.
                  </p>
                  <button
                    type="button"
                    style={baseStyles.primaryButton}
                    onClick={handlePayOnline}
                    disabled={submitting || !razorpayKeyId}
                  >
                    {submitting ? 'Processing...' : 'Pay Online'}
                  </button>
                </div>
              </div>

              <div style={baseStyles.actions} className="opd-form-actions">
                <button
                  type="button"
                  style={baseStyles.secondaryButton}
                  onClick={() => setStep('form')}
                  disabled={submitting}
                >
                  Back to Edit
                </button>
              </div>
            </div>
          ) : null}

          {step === 'complete' ? (
            <div role="status" aria-live="polite">
              <h3 style={baseStyles.sectionTitle}>Thank You!</h3>
              <div style={baseStyles.successBanner}>{success}</div>
              <p style={baseStyles.mutted}>No payment has been collected. Our team will contact you to confirm your appointment.</p>
              <div style={baseStyles.actions} className="opd-form-actions">
                <button
                  type="button"
                  style={baseStyles.secondaryButton}
                  onClick={() => {
                    setForm({
                      patient_name: '',
                      contact_number: '',
                      appointment_date: '',
                      gender: '',
                      email: '',
                      department: '',
                      doctor: '',
                      symptoms: '',
                    });
                    setBooking(null);
                    setSuccess('');
                    setError('');
                    setStep('form');
                  }}
                >
                  Request Another Appointment
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default OpdForm;
